import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import WindowForm from "./WindowForm";
import { useWindows } from "../../hooks/useWindows"; // Hook personalizado
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import { useAuthContext } from "../../context/AuthProvider";

const WindowsDashboard = () => {
  const [open, setOpen] = useState(false);
  const [editWindow, setEditWindow] = useState(null); // Estado para edición
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

  // Hook para obtener las ventanas y las mutaciones
  const { user } = useAuthContext();
  const { windows, isLoading, error, createMutation, updateMutation, deleteMutation } = useWindows();

  // Mostrar mensaje de alerta
  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  // Función para manejar la creación o edición
  const handleSubmitWindow = (data) => {
    
    if (!user) {
      showAlert("Debe iniciar sesión para realizar esta acción", "error");
      return;
    }

    if (editWindow) {
      updateMutation.mutate(
        { id: editWindow.venid, data: { ...data, updbyid: user.EmpID } },
        {
          onSuccess: () => {
            showAlert("Ventana actualizada con éxito");
            setOpen(false);
            setEditWindow(null);
          },
          onError: (error) => {
            showAlert(error.message || "Error al actualizar la ventana", "error");
          },
        }
      );
    } else {
      data.crebyid = user.EmpID;
      createMutation.mutate(data, {
        onSuccess: () => {
          showAlert("Ventana creada con éxito");
          setOpen(false);
        },
        onError: (error) => {
          showAlert(error.message || "Error al crear la ventana", "error");
        },
      });
    }
  };

  // Función para desactivar una ventana
  const handleDeactivateWindow = (id) => {
    deleteMutation.mutate(id, {
      onSuccess: () => showAlert("Ventana desactivada con éxito"),
      onError: (error) => showAlert(error.response.data.error || "Error al desactivar la ventana", "error"),
    });
  };

  if (isLoading) return <Typography>Cargando ventanas...</Typography>;
  if (error) return <Typography>Error al cargar ventanas: {error.message}</Typography>;

  return (
    <Paper sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Ventanas</Typography>
        <Tooltip title="Agregar Ventana">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setEditWindow(null);
              setOpen(true);
            }}
          >
            Nueva Ventana
          </Button>
        </Tooltip>
      </Box>

      {/* Lista de ventanas */}
      <List>
        {windows.map((window) => (
          <ListItem
            key={window.venid}
            sx={{ bgcolor: "white", mb: 1, borderRadius: 1, boxShadow: 1 }}
          >
            <ListItemText
              primary={window.vennom}
              secondary={
                <>
                  <Typography variant="body2" color="text.secondary">
                    URL: {window.venurl}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Controlador: {window.venctr}
                  </Typography>
                </>
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Estado de la ventana */}
              <Chip
                label={window.venest}
                color={window.venest === "ACTIVO" ? "success" : "error"}
                size="small"
              />
              {/* Botón para editar */}
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditWindow(window);
                    setOpen(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {/* Botón para desactivar */}
              {window.venest === "ACTIVO" && (
                <Tooltip title="Desactivar">
                  <IconButton
                    size="small"
                    onClick={() => handleDeactivateWindow(window.venid)}
                  >
                    <BlockIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Formulario para crear/editar ventanas */}
      {open && (
        <WindowForm
          open={open}
          handleClose={() => {
            setOpen(false);
            setEditWindow(null);
          }}
          onSubmit={handleSubmitWindow}
          defaultValues={editWindow || {}}
        />
      )}

      {/* Notificaciones */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default WindowsDashboard;
